import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { FeatureErrorBoundary } from '@/components/app-shell/FeatureErrorBoundary';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import { ROUTES } from '@/utils/routeUtils';
import { useOfficeContextStore } from '@/utils/stores/useOfficeContextStore';

import {
  AssistantBottomSection,
  type AssistantBottomSectionHandle,
} from '../features/assistant/components/AssistantBottomSection';
import { AssistantMainContent } from '../features/assistant/components/AssistantMainContent';
import { AssistantSessionActions } from '../features/assistant/components/AssistantSessionActions';
import { type Suggestion } from '../features/assistant/components/SuggestionCards';
import { useChat } from '../features/assistant/hooks/useChat';

export function AssistantPage() {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();

  const [showContextDialog, setShowContextDialog] = useState(false);
  const bottomSectionRef = useRef<AssistantBottomSectionHandle>(null);

  const setSelectedCustomEmails = useOfficeContextStore((s) => s.setSelectedCustomEmails);
  const setSelectedFilterMode = useOfficeContextStore((s) => s.setSelectedFilterMode);
  const setSessionContext = useOfficeContextStore((s) => s.setSessionContext);
  const setCurrentSessionId = useOfficeContextStore((s) => s.setCurrentSessionId);
  const { contextInfo, refreshContext } = useOfficeContextStore();

  const { clearMessages, handleSend, isLoading, messages, status, stop } = useChat({
    urlSessionId,
  });

  const prevSessionIdRef = useRef<string | null>(null);
  const prevContextRef = useRef<{ conversationId: string | null; itemId: string | null } | null>(
    null,
  );

  const resetToNewSession = useCallback(async () => {
    if (isLoading) {
      stop();
    }
    clearMessages();
    bottomSectionRef.current?.clearState();
    setSessionContext(undefined);
    setSelectedCustomEmails([]);
    setCurrentSessionId(null);
    navigate(ROUTES.ASSISTANT, { replace: true });
    setSelectedFilterMode(null);
  }, [
    clearMessages,
    isLoading,
    navigate,
    setCurrentSessionId,
    setSelectedCustomEmails,
    setSelectedFilterMode,
    setSessionContext,
    stop,
  ]);

  useEffect(() => {
    setSelectedFilterMode(null);
    return () => {
      setSelectedFilterMode(null);
    };
  }, [setSelectedFilterMode]);

  useEffect(() => {
    if (urlSessionId) {
      if (prevSessionIdRef.current !== null && prevSessionIdRef.current !== urlSessionId) {
        clearMessages();
        bottomSectionRef.current?.clearState();
        setSelectedFilterMode(null);
        setSelectedCustomEmails([]);
      }
      prevSessionIdRef.current = urlSessionId;
      setCurrentSessionId(urlSessionId);
    } else {
      if (prevSessionIdRef.current !== null) {
        clearMessages();
        bottomSectionRef.current?.clearState();
        setSelectedFilterMode(null);
        setSelectedCustomEmails([]);
      }
      prevSessionIdRef.current = null;
      setCurrentSessionId(null);
    }
  }, [
    urlSessionId,
    clearMessages,
    setCurrentSessionId,
    setSelectedCustomEmails,
    setSelectedFilterMode,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        try {
          refreshContext();
        } catch {
          /* ignore */
        }
      }
    };
    const handleFocus = () => {
      try {
        refreshContext();
      } catch {
        /* ignore */
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshContext]);

  useEffect(() => {
    if (showContextDialog) {
      try {
        refreshContext();
      } catch {
        /* ignore */
      }
    }
  }, [showContextDialog, refreshContext]);

  useEffect(() => {
    try {
      const currentItemId = contextInfo.itemId;
      const currentConversationId = contextInfo.conversationId;

      if (prevContextRef.current === null) {
        prevContextRef.current = { conversationId: currentConversationId, itemId: currentItemId };
        return;
      }

      const { conversationId: prevConversationId, itemId: prevItemId } = prevContextRef.current;
      const contextChanged =
        (currentItemId !== prevItemId || currentConversationId !== prevConversationId) &&
        (currentItemId !== null || currentConversationId !== null);

      if (contextChanged) {
        setSelectedFilterMode(null);
        setSelectedCustomEmails([]);
        bottomSectionRef.current?.clearState();
      }

      prevContextRef.current = { conversationId: currentConversationId, itemId: currentItemId };
    } catch {
      /* ignore */
    }
  }, [
    contextInfo.itemId,
    contextInfo.conversationId,
    setSelectedFilterMode,
    setSelectedCustomEmails,
  ]);

  const handleSuggestionSelect = useCallback(
    (suggestion: Suggestion) => {
      posthog.capture(ANALYTICS_EVENTS.ASSISTANT_SUGGESTION_CLICKED, {
        suggestionId: suggestion.id,
        suggestionTitle: suggestion.title,
      });
      bottomSectionRef.current?.setPrompt(suggestion.title);
    },
    [posthog],
  );

  const handleAttachmentsLoaded = useCallback(
    (
      attachments: Array<{
        fileName: string;
        fileSize?: number;
        id: string;
        mimeType?: string;
        url?: string;
      }>,
    ) => {
      bottomSectionRef.current?.mergeApiAttachments(attachments);
    },
    [],
  );

  return (
    <div className="bg-back-bg2 flex h-full flex-1 flex-col overflow-hidden">
      <div className="max-w-page mx-auto flex h-full w-full flex-col overflow-hidden">
        <FeatureErrorBoundary name="Session Actions">
          <AssistantSessionActions onNewSession={resetToNewSession} />
        </FeatureErrorBoundary>

        <FeatureErrorBoundary name="Chat">
          <AssistantMainContent
            isLoading={status === 'submitted'}
            isStreaming={status === 'streaming'}
            messages={messages}
            onAttachmentsLoaded={handleAttachmentsLoaded}
            onContextOpenChange={setShowContextDialog}
            onSuggestionSelect={handleSuggestionSelect}
            showContextDialog={showContextDialog}
          />
        </FeatureErrorBoundary>

        <FeatureErrorBoundary name="Message Input">
          <AssistantBottomSection
            isLoading={isLoading}
            onSend={handleSend}
            onStop={stop}
            ref={bottomSectionRef}
            setShowContextDialog={setShowContextDialog}
            showContextDialog={showContextDialog}
          />
        </FeatureErrorBoundary>
      </div>
    </div>
  );
}
